import { gql } from '@apollo/client';
import { ValidatingInput, LabeledInputProps } from '~/components/validating-input';
import { LDClient, LDFlags } from '~/providers';
import { store } from '~/utils/store';
import { trackError } from '~/utils/tracking';
import { isUSMobileNumber } from './phone.utils';

export const VALIDATE = gql`
  query Flex_ValidatePhoneNumber($countryCode: String!, $phoneNumber: String!) {
    validation {
      phoneNumber(countryCode: $countryCode, phoneNumber: $phoneNumber) {
        response
        isValid
        error {
          message
          status
        }
      }
    }
  }
`;

export const Phone = ({ onChange, ...props }: LabeledInputProps) => {
  const validate = async ({ apollo, value }) => {
    const invalidPhoneError = 'Please enter a valid phone number';
    const parsedPhoneNumber = parseDisplayValue(value);
    if (parsedPhoneNumber.length !== 10) return invalidPhoneError;

    if (LDClient.variation(LDFlags.useTwilioVerifyInInputPage)) {
      const { data } = await apollo.query({
        fetchPolicy: 'no-cache',
        query: VALIDATE,
        variables: { countryCode: 'US', phoneNumber: parsedPhoneNumber },
      });
      const { response, isValid } = data.validation.phoneNumber;

      if (!isValid) {
        const { error } = data.validation.phoneNumber;
        if (!error) return invalidPhoneError;
        const { message, status } = error;

        if (status === 404) {
          return invalidPhoneError;
        } else {
          const twilioApiError = 'Twilio API failed';
          trackError(new Error(twilioApiError), {
            extras: { message, status },
          });
          return isUSMobileNumber(parsedPhoneNumber, invalidPhoneError);
        }
      }

      store.set('META_DATA', response);
    } else {
      return isUSMobileNumber(parsedPhoneNumber, invalidPhoneError);
    }
    return true;
  };

  const handleChange = (value, params) => onChange(parseDisplayValue(value), params);

  return (
    <ValidatingInput
      autoComplete="tel-national"
      onChange={handleChange}
      placeholder="888-555-5555"
      type="tel"
      validate={validate}
      {...props}
    />
  );
};

const nonDigits = /\D/g;
const parseDisplayValue = str => {
  const digits = str.replace(nonDigits, '');
  if (digits.length === 11 && digits[0] === '1') {
    return digits.substring(1);
  } else {
    return digits;
  }
};
